import { Menu, Transition } from '@headlessui/react'
import { Fragment, useEffect, useState } from 'react'
import { Home, Settings, User2, Image, FileText, LogOut } from 'lucide-react'
import { PiUserCircle } from 'react-icons/pi'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import CookieManager from '../lib/CookiesManager'
import AgeRestrictionModal from './AgeCookiesRestrictionModal'
import LoginTooltip from './LoginTooltip'
import { ScreenModeType } from '../pages/LoginRegisterPages'

interface MenuLoginProps {
  className?: string
}

const loggedInLinks = [
  {
    href: '/dashboard/',
    labelKey: 'dashboard.dashboard',
    icon: Home,
    color: 'text-redpink',
  },
  {
    href: '/dashboard/settings/',
    labelKey: 'dashboard.account-settings',
    icon: Settings,
    color: 'text-anthracite',
  },
  {
    href: '/dashboard/profile',
    labelKey: 'dashboard.profile',
    icon: User2,
    color: 'text-blue-500',
  },
  {
    href: '/dashboard/my-gallery/',
    labelKey: 'dashboard.my-gallery',
    icon: Image,
    color: 'text-green-500',
  },
  {
    href: '/dashboard/announces/',
    labelKey: 'dashboard.my-ads',
    icon: FileText,
    color: 'text-orange-500',
  },
  {
    href: '/login',
    labelKey: 'disconnect',
    icon: LogOut,
    color: 'text-red-500',
  },
]

const loggedOutLinks = [
  {
    href: '/login',
    labelKey: 'login',
    icon: User2,
    color: 'text-redpink',
  },
  {
    href: '/register',
    labelKey: 'register',
    icon: FileText,
    color: 'text-emerald-500',
  },
]

export default function MenuLogin({ className }: MenuLoginProps) {
  const { t } = useTranslation()
  const location = useLocation()
  const [isLoggedIn, setIsLoggedIn] = useState(() =>
    CookieManager.isAuthenticated(),
  )
  const links = isLoggedIn ? loggedInLinks : loggedOutLinks
  const [showTooltip, setShowTooltip] = useState(false)
  const [showAgeModal, setShowAgeModal] = useState(false)
  const isAgeVerified = CookieManager.isAgeVerified()

  useEffect(() => {
    if (!isAgeVerified) {
      setShowAgeModal(true)
    } else if (!CookieManager.isLoginHintHidden() && !isLoggedIn) {
      const showTimer = setTimeout(() => {
        setShowTooltip(true)
      }, 1000)

      const hideTimer = setTimeout(() => {
        setShowTooltip(false)
      }, 9000)

      return () => {
        clearTimeout(showTimer)
        clearTimeout(hideTimer)
      }
    }
  }, [isLoggedIn, isAgeVerified])

  useEffect(() => {
    const consent = CookieManager.getConsent()
    const isAgeVerified = CookieManager.isAgeVerified()
    if (!consent.functional || !isAgeVerified) {
      setShowAgeModal(true)
    }
  }, [])

  const handleCloseAgeModal = () => {
    setShowAgeModal(false)

    if (!CookieManager.isLoginHintHidden() && !isLoggedIn) {
      const showTimer = setTimeout(() => {
        setShowTooltip(true)
      }, 1000)

      const hideTimer = setTimeout(() => {
        setShowTooltip(false)
      }, 9000)

      return () => {
        clearTimeout(showTimer)
        clearTimeout(hideTimer)
      }
    }
  }

  return (
    <>
      {!isAgeVerified && (
        <AgeRestrictionModal
          isOpen={showAgeModal}
          onClose={handleCloseAgeModal}
        />
      )}

      <Menu as='div' className={`relative inline-block text-left ${className}`}>
        <div className='relative'>
          <Menu.Button className='inline-flex w-full hover:text-redpink items-center justify-center rounded-md text-3xl font-base text-white shadow-sm focus:outline-none transition-all duration-200'>
            <PiUserCircle className='relative cursor-pointer' />
          </Menu.Button>

          {showTooltip &&
            !CookieManager.isLoginHintHidden() &&
            !CookieManager.isAuthenticated() &&
            location.pathname === '/' && (
              <LoginTooltip
                onClose={() => {
                  setShowTooltip(false)
                  CookieManager.setLoginHintHidden()
                }}
                onSwitchMode={function (mode: ScreenModeType): void {
                  throw new Error('Function not implemented.')
                }}
              />
            )}
        </div>

        <Transition
          as={Fragment}
          enter='transition ease-out duration-100'
          enterFrom='transform opacity-0 scale-95'
          enterTo='transform opacity-100 scale-100'
          leave='transition ease-in duration-75'
          leaveFrom='transform opacity-100 scale-100'
          leaveTo='transform opacity-0 scale-95'
        >
          <Menu.Items className='absolute right-0 z-50 mt-0 w-56 top-9 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none divide-y divide-gray-100'>
            <div className='px-1 py-1'>
              {links.map(link => {
                const Icon = link.icon
                return (
                  <Menu.Item key={link.href}>
                    {({ active }) => (
                      <Link
                        to={link.href}
                        className={`${
                          active ? 'bg-grisclair' : ''
                        } group flex w-full items-center rounded-md px-3 py-2 text-sm transition-all duration-200`}
                      >
                        <Icon
                          className={`mr-3 h-5 w-5 ${link.color}`}
                          aria-hidden='true'
                        />
                        <span
                          className={`${
                            active ? 'text-anthracite' : 'text-anthracite'
                          }`}
                        >
                          {t(link.labelKey)}
                        </span>
                      </Link>
                    )}
                  </Menu.Item>
                )
              })}
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </>
  )
}
